import React from 'react';
import SIDEBAR_CONFIG from './sidebarConfig';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  Tooltip,
  Menu,
  MenuItem,
  useMediaQuery,
  capitalize,
  Grid,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PerfectScrollbar from 'react-perfect-scrollbar';
import Cookies from 'js-cookie';

import SideBarLogo from '../../assets/new_backrr_logo.svg';
import { ReactComponent as AnalyticsIcon } from '../../assets/nav_icons/rocket-svgrepo-com.svg';
import { ReactComponent as UserIcon } from '../../assets/nav_icons/person-male-svgrepo-com.svg';
import { ReactComponent as ReportsIcon } from '../../assets/nav_icons/reports.svg';
import { ReactComponent as SearchInvestorsIcon } from '../../assets/nav_icons/search-find-user-svgrepo-com.svg';
import { ReactComponent as PortfolioIcon } from '../../assets/nav_icons/chart-diagram-pie-svgrepo-com.svg';
import { ReactComponent as SettingsIcon } from '../../assets/nav_icons/settings_nav_icon2.svg';
import { ReactComponent as DealFlowIcon } from '../../assets/nav_icons/funnel-plot.svg';
import { ReactComponent as ReelsIcon } from '../../assets/nav_icons/reels.svg';
import { ReactComponent as ToolsIcon } from '../../assets/nav_icons/tools.svg';
// Copied from the senior's startup-web (layout/Sidebar/components/SidebarIcons.js →
// BackrrPageIcon): same 17x17 canvas, same path, same 1.3 stroke.
import { ReactComponent as BackrrPageIcon } from '../../assets/nav_icons/backrr_page.svg';
import { SET_MENU, RESET_STATE } from '../../store/actions';
import { capitalizedSentence, checkForDomain, getTemplateKeyValue, hasPermission, isSelectedInvestorGroup } from '../../utils';
import { FetchUserAssociatedInvestorAccounts } from 'api/permission';
import ImageAvatar from 'ui-components/ImageAvatar';
import NavItemType from './MenuList/NavItem/NavItemType';
import SidebarNavigation from './components/SidebarNavigation';
import SidebarNavItem from './SidebarNavItem';
import { FetchLatestVersion } from 'api/version';
import { type } from '@testing-library/user-event/dist/type';
import { BuildOutlined } from '@mui/icons-material';
import { ReceiptTextIcon, CreditCard, Users, LogOut, ChevronRight } from 'lucide-react';
import LogoutConfirmationDialog from 'ui-components/popups/DeleteConfirmationPopup';

const Sidebar = () => {
  const { refetch: refetchLatestVersion } = FetchLatestVersion();
  const currentIP = window.location.host;
  let [investor_id, setInvestorID] = React.useState(Cookies.get("X-INV-ID") || null);
  const investor_types = useSelector((state) => state?.investorDetails?.investor_types);
  const { data: userAssociatedInvestorAccounts, isLoading: isLoadingUserAssociatedInvestorAccounts, refetch: refetchUserAssociatedInvestorAccounts } = FetchUserAssociatedInvestorAccounts();
  const userAccess = useSelector((state) => state?.permissions);
  const currentInvestorType = useSelector((state) => state.investorDetails.data)?.investor_type;

  // Angel / super angel invest as individuals; every other type (VC, Micro VC, Family
  // Office, Accelerator, Incubator, Investment Banker) is an organisation, so its profile
  // is the org's page - "Org Profile" with startup-web's Backrr Page icon.
  // Declared here, above the nav config that reads it - not next to the other footer
  // helpers further down, which would be a use-before-initialisation error.
  const isOrgProfile = Boolean(currentInvestorType) && !currentInvestorType?.includes('angel');

  // Grouped to match the design: Deal Flow / Portfolio / Intelligence, with Settings and
  // Billing & Plan pinned to the bottom. Every title, path and permission gate is unchanged
  // from the old flat NavItemsTop - only the grouping and the Tools label are new.
  const NavSections = [
    {
      label: 'Deal Flow',
      items: [
        { title: getTemplateKeyValue({ module: "startups", template: userAccess?.template }), icon: DealFlowIcon, path: '/', useFill: true, permission: hasPermission("startups", "view", userAccess?.userPermissions), type: "item" },
        { title: getTemplateKeyValue({ module: "pitch_reels", template: userAccess?.template }), icon: ReelsIcon, path: '/pitch-reels', useFill: true, permission: hasPermission("pitch_reels", "view", userAccess?.userPermissions), type: "item" },
        // Not in the design, but a real shipped feature - kept, and placed here pending a call on where it belongs.
        { title: getTemplateKeyValue({ module: "startups_new", template: userAccess?.template }), icon: AnalyticsIcon, path: '/startups', useFill: true, permission: hasPermission("startups_new", "view", userAccess?.userPermissions) && currentInvestorType && (investor_types)?.length > 0 && isSelectedInvestorGroup({ type: currentInvestorType, investor_types }), type: "item" },
      ],
    },
    {
      label: 'Portfolio',
      items: [
        { title: getTemplateKeyValue({ module: "portfolio", template: userAccess?.template }), icon: PortfolioIcon, path: '/portfolio', useFill: true, permission: hasPermission("portfolio", "view", userAccess?.userPermissions), type: "item" },
        // Flat leaf: its only child ("All Reports") pointed at /reports too, so the
        // expander just hid a second copy of this same link. Nothing is a parent now -
        // add `children` back to an item when it genuinely has sub-pages.
        { title: getTemplateKeyValue({ module: "reports", template: userAccess?.template }), icon: ReportsIcon, path: '/reports', useFill: true, permission: hasPermission("reports", "view", userAccess?.userPermissions), type: "item" },
      ],
    },
    {
      label: 'Intelligence',
      items: [
        { title: "Pitch Deck Analyzer", icon: ToolsIcon, path: '/bulk-analysis', useStroke: true, permission: true, type: "item" },
      ],
    },
  ];

  // Bottom group. Billing & Plan is rendered but inert until its pages exist; Switch Account
  // only appears for users who actually have more than one investor account.
  const NavItemsBottomGroup = [
    // Individuals keep the template's "Profile" label + person icon; organisations get
    // "Org Profile" + the Backrr Page icon. Same path and permission gate either way.
    { title: isOrgProfile ? "Org Profile" : getTemplateKeyValue({ module: "profile", template: userAccess?.template }), icon: isOrgProfile ? BackrrPageIcon : UserIcon, path: '/profile', useStroke: true, permission: hasPermission("profile", "view", userAccess?.userPermissions), type: "item" },
    { title: getTemplateKeyValue({ module: "settings", template: userAccess?.template }), icon: SettingsIcon, path: '/settings', useStroke: true, permission: hasPermission("settings", "view", userAccess?.userPermissions), type: "item" },
    // Placeholder until its pages exist - hidden for now, uncomment to bring it back.
    // { title: "Billing & Plan", icon: CreditCard, path: undefined, useStroke: true, permission: true, disabled: true, type: "item" },
    ...(userAssociatedInvestorAccounts && userAssociatedInvestorAccounts?.length > 1
      // Wrapped, not passed by reference: handleAvatarClick is declared further down, so a
      // bare reference here would be read before initialisation.
      ? [{ title: "Switch Account", icon: Users, useStroke: true, permission: true, type: "item", onClick: (e) => handleAvatarClick(e) }]
      : []),
  ];

  const NavItemsBottom = Cookies.get("permissions") ? [
    // { title: getTemplateKeyValue({ module: "profile", template: userAccess?.template }), icon: UserIcon, path: '/profile', useStroke: true, permission: hasPermission("profile", "view", userAccess?.userPermissions), type: "item" },
  ] : [
    // { title: getTemplateKeyValue({ module: "profile", template: userAccess?.template }), icon: UserIcon, path: '/profile', useStroke: true, permission: hasPermission("profile", "view", userAccess?.userPermissions), type: "item" },
  ]
  const theme = useTheme();
  const matchesUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const open = useSelector((state) => state.customization.opened);
  const details = useSelector((state) => state?.investorDetails?.data);

  const investorTypeLabel = investor_types?.find(item => item?.value === details?.investor_type)?.label ?? 
     'Angel Investor';

  const toggleDrawer = () => {
    refetchLatestVersion();
    dispatch({ type: SET_MENU, opened: !open });
  };

  const path = location.pathname;

  const [anchorEl, setAnchorEl] = React.useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
    refetchUserAssociatedInvestorAccounts()
  };

  const handleMenuClose = ({ _id }) => {
    if (_id && investor_id !== _id) {
      Cookies.set("X-INV-ID", _id, { domain: checkForDomain() });
      if (currentIP?.includes("localhost")) {
        Cookies.set("X-INV-ID", _id);
      }
      setInvestorID(_id);
    }
    setAnchorEl(null);
    if (_id && investor_id !== _id) {
      window.location.reload();
    }
  };

  // Logging out wipes every cookie and drops the session, so it asks first - same
  // confirmation startup-web's sidebar shows. `handleLogOut` itself is unchanged; it is
  // just no longer wired straight to a click.
  const [openLogoutConfirmation, setOpenLogoutConfirmation] = React.useState(false);
  const handleLogoutConfirmationOpen = () => setOpenLogoutConfirmation(true);
  const handleLogoutConfirmationClose = () => setOpenLogoutConfirmation(false);

  const handleLogOut = () => {
    setOpenLogoutConfirmation(false);
    dispatch({ type: RESET_STATE });
    Cookies.remove('X-INV-JWT', { path: '/' });
    Cookies.remove('X-INV-JWT', { domain: checkForDomain() });
    Cookies.remove('X-INV-ID', { path: '/' });
    Cookies.remove('X-INV-ID', { domain: checkForDomain() });
    Cookies.remove('name', { path: '/' });
    Cookies.remove('user_id', { path: '/' });
    Cookies.remove('startup_id', { path: '/' });
    Cookies.remove('email', { path: '/' });
    Cookies.remove('is_profile_complete', { path: '/' });
    Cookies.remove('startup_id', { path: '/' });
    Cookies.remove('step', { path: '/' });
    Cookies.remove('permissions', { path: '/' });
    Cookies.remove('ur', { path: '/' });
    Cookies.remove('search_startups', { path: '/' });
    Cookies.remove('search_backrr_startups', { path: '/' });
    Cookies.remove('backrr_startups_query', { path: '/' });
    Cookies.remove('backrr_startups_status', { path: '/' });
    const cookies = document.cookie.split(";");
    cookies.forEach(cookie => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
    });
    navigate('/login');
  };

  const handleNavigate = (path) => {
    navigate(path)
    handleMenuClose({ _id: null })
  }

  console.log(investor_types, "investor_types")

  const SwitchAccountsMenu = () => {
    // One account = nothing to switch to, so the row is information only.
    const canSwitchAccounts = (userAssociatedInvestorAccounts?.length || 0) > 1;

    return <Menu
      anchorEl={anchorEl}
      open={isMenuOpen}
      onClose={handleMenuClose}
      transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
      slotProps={{ paper: { sx: { marginTop: '-12px' } } }}
      sx={{
        '& .MuiPaper-root': {
          minWidth: '18.75rem',
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        },
        '& .MuiList-root': {
          paddingTop: 0,
          paddingBottom: 0,
        },
      }}
    >
      <>
        {/* {hasPermission("settings", "manage_users", userAccess?.userPermissions) && <MenuItem
          onClick={() => handleNavigate("/settings")}
          sx={{
            paddingBlock: "0.5rem",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <SettingsIcon stroke="#3c3c3c" />
          <Typography sx={{ fontWeight: "bold", color: "#3c3c3c" }}>
            Account Settings
          </Typography>
        </MenuItem>} */}
        
        {/* Orders & Invoices and Sign Out are commented out: this menu is now reached from
            the Switch Account nav item, so it only lists accounts. Orders & Invoices moves
            under Billing & Plan later; Sign Out already lives on the profile card. */}
        {/* <MenuItem onClick={() => handleNavigate('/orders-invoices')} sx={{
          paddingBlock: "0.5rem",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}>
          <ReceiptTextIcon stroke="#3c3c3c" width={24} height={24} />
          <Typography sx={{ fontWeight: "bold", color: "#3c3c3c" }}>
            Orders & Invoices
          </Typography>
        </MenuItem>
        <MenuItem onClick={handleLogoutConfirmationOpen} sx={{
          paddingBlock: "0.5rem",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}>
          <LogoutIcon stroke="#3c3c3c" />
          <Box sx={{ color: "#3c3c3c", fontWeight: 'bold', }}>Sign Out</Box>
        </MenuItem>
        <Divider /> */}
      </>
      {/* Layout ported from the senior's startup-web SwitchAccountsMenu
          (layout/Sidebar/components/SwitchAccountsMenu.js), mapped onto the investor
          shape (investor_id / investor_name / profile_picture). Behaviour unchanged:
          handleMenuClose({ _id }) still drives the X-INV-ID switch. */}
      {userAssociatedInvestorAccounts && userAssociatedInvestorAccounts?.length > 0 && (
        <Box sx={{ padding: '0.875rem 1rem', borderBottom: 'thin solid #ebebeb !important' }}>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#3c3c3c' }}>
            Your Accounts
          </Typography>
        </Box>
      )}
      {userAssociatedInvestorAccounts && userAssociatedInvestorAccounts?.map((investor) => {
        const isActive = investor?.investor_id === investor_id;
        return (
          <MenuItem
            key={investor?.investor_id}
            disableRipple={!canSwitchAccounts}
            onClick={
              canSwitchAccounts
                ? () => { handleMenuClose({ _id: investor?.investor_id }); }
                : undefined
            }
            sx={{
              padding: '0.875rem 1rem',
              gap: 1.5,
              cursor: canSwitchAccounts ? 'pointer' : 'default',
              background: isActive && canSwitchAccounts ? '#f3f0ff' : 'transparent',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: canSwitchAccounts
                  ? '#f9f9f9'
                  : isActive && canSwitchAccounts
                    ? '#f3f0ff'
                    : 'transparent',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
              <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <ImageAvatar
                  width={'2.75rem'}
                  height={'2.75rem'}
                  style={{
                    width: '2.75rem',
                    height: '2.75rem',
                    borderRadius: '6px',
                    mixBlendMode: 'multiply',
                  }}
                  src={investor?.profile_picture || ''}
                  alt={`${investor?.investor_name || ''}`}
                  tooltip={false}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '0.875rem', fontWeight: 700, color: '#3c3c3c',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >
                  {investor?.investor_name || 'Unnamed account'}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#999', marginTop: '0.125rem' }}>
                  {capitalize(investor?.user_role || '')}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        )
      })}
      {/* Rendered unconditionally and last. The accounts list above is driven by
          userAssociatedInvestorAccounts, which can be empty - logging out must not
          depend on how many accounts a user has. Closes the menu first, then opens
          the same confirmation the footer icon used to. */}
      <MenuItem
        onClick={() => { handleMenuClose({ _id: null }); handleLogoutConfirmationOpen(); }}
        sx={{
          padding: '0.875rem 1rem',
          gap: 1.25,
          color: '#3c3c3c',
          '&:hover': { backgroundColor: '#f9f9f9' },
          borderTop: 'thin solid #ebebeb !important',
        }}
      >
        <LogOut size={16} />
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 700 }}>
          Logout
        </Typography>
      </MenuItem>
    </Menu>
  }

  // The investor_image state that used to live here fed only the footer ImageAvatar's
  // `src`. That avatar is initials-only now, so tracking the image was dead work.

  return (
    <Box component="nav" aria-label="sidebar navigation">
      <Drawer
        variant={matchesUpMd ? 'permanent' : 'temporary'}
        open={matchesUpMd ? false : open}
        onClose={toggleDrawer}
        // ModalProps={{ keepMounted: true }}
        sx={{
          height: '100dvh',
          '& .MuiDrawer-paper': {
            width: matchesUpMd
              ? `${SIDEBAR_CONFIG.desktop.expanded}px`
              : SIDEBAR_CONFIG.mobile.temporary,
            height: '100%',
            backgroundColor: '#000000',
            color: '#FFFFFF',
            borderRight: 'none',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            transition: 'width 0.3s ease',
            position: matchesUpMd ? 'relative' : 'fixed',
            padding: '0.5rem',
          },
        }}
      >
        {/* Logo block ported from the senior's live SidebarHeader: a single inverted wordmark
            rather than a square icon + separate "Backrr" text, which is what threw the
            alignment off against startup-web. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: matchesUpMd ? 'flex-start' : 'space-between',
            mt: { xs: '0.5rem', md: '1.5rem' },
            mb: { xs: '1.5rem', md: '2.5rem' },
            px: '0rem',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              minWidth: 0,
              height: '1rem',
              padding: '0.5rem 1.2rem',
              marginTop: '0.75rem',
            }}
          >
            <img
              src={SideBarLogo}
              alt="Backrr"
              style={{ height: '2rem', width: 'auto', objectFit: 'contain', flexShrink: 0 }}
            />
          </Box>
          {!matchesUpMd && (
            <IconButton onClick={toggleDrawer} sx={{ color: '#FFFFFF' }}>
              ✕
            </IconButton>
          )}
        </Box>

        {/* Nav rendering ported from the senior's live startup-web sidebar (SidebarNavigation
            + SidebarNavItem). Titles still come from getTemplateKeyValue and each item keeps
            its own permission gate - only the grouping into sections is new.
            Rendered as a direct flex child, exactly as startup-web's NewSidebar does: it
            already owns `flex: 1` + `overflowY: auto`. It used to be wrapped in a
            PerfectScrollbar with `display: grid`, which swallowed that `flex: 1` (the
            wrapper became the flex child instead), so the footer never got pushed to the
            bottom of the drawer. */}
        <SidebarNavigation
          sections={NavSections}
          sidebarCollapsed={false}
          matchesUpMd={matchesUpMd}
          toggleDrawer={toggleDrawer}
        />

        {/* Footer - mirrors startup-web's layout/Sidebar/components/SidebarFooter.js
            structure and spacing exactly. Previously this had an extra <Divider>, a
            `pt: 1` / `pb: 2` wrapper and a `mt: 0.5rem` on the separator, which added
            ~32px the senior's version doesn't have. */}
        <Box sx={{ flexShrink: 0 }}>
          <Box sx={{ px: '0', pb: '0.25rem' }}>

          {/* Bottom nav group (Profile / Settings / Billing & Plan / Switch Account).
              Rendered OUTSIDE the mobile/desktop branch: Profile and Settings used to sit
              in NavItemsTop, which renders in both layouts, so keeping this inside the
              desktop branch dropped them from the mobile drawer entirely.
              drawerToggle closes the drawer after tapping an item on mobile. */}
          {NavItemsBottomGroup.filter((item) => item?.permission !== false).map((item) => (
            <SidebarNavItem key={item.title} {...item} collapsed={false} drawerToggle={!matchesUpMd ? toggleDrawer : undefined} />
          ))}
          </Box>

            {/* One profile card at every breakpoint, identical to startup-web's
                SidebarFooter. The mobile drawer used to render a bare avatar + logout
                row with no name at all, so the user's name only ever showed on desktop.
                It fits: the mobile drawer is 280px (sidebarConfig) and the name block
                ellipsises via minWidth: 0. */}
            <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', mx: '0.875rem' }} />
            {/* Whole card opens the account-switch menu, same as startup-web's footer. */}
            <Box
              onClick={handleAvatarClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.625rem',
                px: '0.875rem',
                py: '0.75rem',
                cursor: 'pointer',
                transition: 'background 0.15s ease',
                borderRadius: '6px',
                mx: '0.25rem',
                mb: '0.375rem',
                mt: '0.25rem',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
              }}
            >
              <ImageAvatar
                height={'2.5rem'}
                width={'2.5rem'}
                style={{ borderRadius: '9px', flexShrink: 0, width: '2.5rem', height: '2.5rem' }}
                // Deliberately no `src`: this footer always shows initials on the
                // name-hashed tint, never the uploaded photo/logo. Those images are
                // authored for a large light card on /profile and read as an
                // unrecognisable dark smudge at 2.5rem on the black sidebar.
                // ImageAvatar renders initials whenever src is absent, so omitting it
                // is all that's needed. `alt` drives both the tint and the initials.
                toolTipPlacement='right'
                tooltip={true}
                alt={userAccess?.userName || ''}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '0.8125rem', fontWeight: 600, color: 'white', lineHeight: 1.25,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >
                  {userAccess?.userName || ''}
                </Typography>
                {details?.investor_type && (
                  <Typography
                    sx={{
                      fontSize: '0.6875rem', fontWeight: 400, color: 'rgba(255,255,255,0.55)', lineHeight: 1.25,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {investorTypeLabel}
                  </Typography>
                )}
              </Box>
              {/* Affordance only - the whole card opens the menu. */}
              <ChevronRight size={13} color="rgba(255,255,255,0.35)" />
            </Box>
            <SwitchAccountsMenu />

        </Box>
      </Drawer>

      {/* Same confirmation startup-web's sidebar uses, wired to both logout triggers
          (the account menu's "Sign Out" and the footer icon). */}
      <LogoutConfirmationDialog
        open={openLogoutConfirmation}
        handleClose={handleLogoutConfirmationClose}
        handleSubmission={handleLogOut}
        heading="Confirm Logout"
        subheading="Are you sure you want to log out of your account?"
        ActionText="Logout"
      />
    </Box>
  );
};

export default Sidebar;